import { Component, Prop, Ref } from 'vue-property-decorator'
import ConfigurationSectionHeader from '@/modules/configurationModule/ConfigurationSectionHeader.vue'
import VtGrid from '@/components/VtGrid/VtGrid.vue'
import { GetContextMenuItemsParams, MenuItemDef } from '@ag-grid-enterprise/all-modules'
import ShipmentRulesModalComponent from '@/modules/configurationModule/detail/shipments/shipment-rules/components/shipment-rules-modal/shipment-rules-modal.vue'
import ShipmentRulesModal from '@/modules/configurationModule/detail/shipments/shipment-rules/components/shipment-rules-modal/shipment-rules-modal'
import BaseCrudServerSideComponent from '@/shared/resources/base-crud-server-side.component'
import { BaseCrud } from '@/shared/models/cruds.models'
import {
    ShipmentRuleCreateRequest,
    ShipmentRulePatchInBatchRequest,
    ShipmentRuleResponse
} from '@/modules/configurationModule/detail/shipments/shipment-rules/services/shipment-rule/shipment-rule.dto'
import {
    columnsDefShipmentRulesGrid,
    initialShipmentRulesGridState,
    ShipmentRulesGridServiceType,
    ShipmentRulesGridState
} from '@/modules/configurationModule/detail/shipments/shipment-rules/components/grid/shipment-rules-grid.model'
import ModalWrapper from '@/components/ModalWrapper.vue'
import { EntityIdentifiersRequest, EntityIdentifierWithErrorResponse } from '@/shared/models/http/http.model'
import { shipmentEntityState } from '@/api/resources/entityState/shipment-entity-state'
import { PaginatorDatasource } from '@/shared/resources/datasources/paginator/paginator-datasource'

@Component({
    components: {
        ConfigurationSectionHeader,
        VtGrid,
        ShipmentRulesModalComponent
    }
})
export default class ShipmentRulesGrid extends BaseCrudServerSideComponent<ShipmentRuleResponse> implements BaseCrud {
    @Ref() readonly shipmentRulesModalWrapper!: typeof ModalWrapper
    @Ref() shipmentRulesModal!: ShipmentRulesModal

    @Prop() readonly isManual!: boolean
    @Prop() readonly serviceInstance!: () => Promise<ShipmentRulesGridServiceType>

    state: ShipmentRulesGridState
    constructor() {
        super()
        this.state = initialShipmentRulesGridState(null)
    }

    async created(): Promise<void> {
        // load the states needed for the grid entity status column filter
        await this.$_toCallUnique(shipmentEntityState.loadStates())
        this.state.statesLoaded = true
    }

    async mounted(): Promise<void> {
        const customService = await this.serviceInstance()
        this.state.columnsDef = columnsDefShipmentRulesGrid(this.isManual)
        this.state.datasource = new PaginatorDatasource(customService)
        this.state.service = customService
    }

    get canUpdate(): boolean {
        return this.state.selectedRows.length === 1
    }

    get canDelete(): boolean {
        return !!this.state.selectedRows.length
    }

    get gridSectionTitle(): string {
        return this.isManual ? this.$tc('configuration.sections.manualShipmentRules')
            : this.$tc('configuration.sections.automaticShipmentRules')
    }

    getContextMenuItems(params: GetContextMenuItemsParams): MenuItemDef[] {
        if (!params?.node) {
            return []
        } else {
            return [
                {
                    name: this.$tc('configuration.actions.update').toString(),
                    cssClasses: this.state.selectedRows.length > 1 ? ['not-show'] : [''],
                    action: () => this.onUpdate(params?.node?.data)
                },
                {
                    name: this.$t('configuration.actions.delete').toString(),
                    cssClasses: this.state.selectedRows.length < 1 ? ['not-show'] : [''],
                    action: this.onDelete
                },
                {
                    name: params?.node?.data?.active ? this.$tc('configuration.actions.deactivate') : this.$tc('configuration.actions.activate'),
                    cssClasses: this.state.selectedRows.length ? ['not-show'] : [''],
                    action: () => this.activateDeactivateSelection(!params?.node?.data?.active, [params?.node?.data])
                },
                {
                    name: this.$tc('configuration.actions.activate'),
                    cssClasses: this.canActivateBatch ? [''] : ['not-show'],
                    action: this.onActivate
                },
                {
                    name: this.$tc('configuration.actions.deactivate'),
                    cssClasses: this.canDeactivateBatch ? [''] : ['not-show'],
                    action: this.onDeactivate
                }
            ]
        }
    }

    async activateDeactivateSelection(option: boolean, selectionBatch: ShipmentRuleResponse[]): Promise<void> {
        const filteredRowActiveState = selectionBatch?.filter((row: ShipmentRuleResponse) => row.active !== option)
        const updatedFilteredRowActiveState: ShipmentRulePatchInBatchRequest[] = filteredRowActiveState?.map((row: ShipmentRuleResponse) => {
            return {
                id: row.id,
                active: option
            }
        })
        if (updatedFilteredRowActiveState?.length) {
            await this.patchOperation(updatedFilteredRowActiveState)
        }
    }

    async onActivate(): Promise<void> {
        await this.activateDeactivateSelection(true, this.state.selectedRows)
    }

    async onDeactivate(): Promise<void> {
        await this.activateDeactivateSelection(false, this.state.selectedRows)
    }

    private async _createShipmentRule(createRequest: ShipmentRuleCreateRequest): Promise<boolean> {
        if (this.state.service) {
            const response = await this.$_toCallUnique(this.state.service.createShipmentRule(createRequest))
            if (response) {
                this.$_showToastSuccess(this.$tc('configuration.toastMessages.created'))
                await this.shipmentRulesModal.updateRow(response)
                return true
            }
        }
        return false
    }

    async createOperation(createRequest: ShipmentRuleCreateRequest): Promise<void> {
        if (createRequest) {
            if (await this._createShipmentRule(createRequest)) {
                this.reloadData()
                this.state.actionTitle = this.$tc('configuration.actions.update')
            }
        }
    }

    async patchOperation(batchRequest: ShipmentRulePatchInBatchRequest[]): Promise<void> {
        if (batchRequest) {
            if (await this._patchShipmentRules(batchRequest)) {
                this.reloadData()
            }
        }
    }

    async deleteOperation(requestDelete: EntityIdentifiersRequest): Promise<void> {
        if (requestDelete) {
            if (await this._showModalAdviceWhenDelete()) {
                if (await this._deleteShipmentRules(requestDelete)) {
                    this.reloadData()
                }
            }
        }
    }

    private async _patchShipmentRules(batchRequests: ShipmentRulePatchInBatchRequest[]): Promise<boolean> {
        if (this.state.service) {
            if (batchRequests) {
                const response = await this.$_toCallUnique(this.state.service?.patchShipmentRulesBatch(batchRequests))
                if (response) {
                    const elements = batchRequests.length - response.length
                    if (elements > 0) {
                        const choice = elements > 1 ? 2 : 1
                        this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated', choice, { elements }))
                    }
                    return true
                }
            }
        }
        return false
    }

    private async _deleteShipmentRules(requestDelete: EntityIdentifiersRequest): Promise<EntityIdentifierWithErrorResponse[] | null> {
        if (this.state.service) {
            const ids = requestDelete?.entityIdentifiers
            if (ids?.length) {
                const response = await this.$_toCallUnique(this.state.service.deleteShipmentRulesBatch(requestDelete))
                if (response) {
                    const elements = ids.length - response.length
                    if (elements > 0) {
                        const choice = elements > 1 ? 2 : 1
                        this.$_showToastSuccess(this.$tc('configuration.toastMessages.deleted', choice, { elements }))
                    }
                    return response
                }
            }
        }
        return null
    }

    private async _showModalAdviceWhenDelete(): Promise<boolean> {
        const labelConfirmation: string = this.$tc('configuration.grid.deleteConfirmation')
        const selectedRowsSize: number = this.state?.selectedRows?.length
        const labelGrid: string = this.$tc('configuration.grid.element', selectedRowsSize)
        const labelQuestion: string = this.$tc('configuration.grid.deleteQuestion')
        return this.$_showConfirmationModal(`${labelConfirmation} ${selectedRowsSize} ${labelGrid}, <br/> ${labelQuestion}`)
    }

    async onDelete(): Promise<void> {
        if (this.state.selectedRows.length) {
            const requestDelete: EntityIdentifiersRequest = {
                entityIdentifiers: this.state.selectedRows.map(shipmentRule => shipmentRule.id)
            }
            await this.deleteOperation(requestDelete)
        }
    }

    onUpdate(shipmentRule: ShipmentRuleResponse = this.state.gridApi?.getSelectedRows()[0]): void {
        if (shipmentRule) {
            this.showModal(this.$tc('configuration.actions.update'), shipmentRule, this.shipmentRulesModalWrapper)
        }
    }

    onAdd(): void {
        this.showModal(this.$tc('configuration.actions.add'), null, this.shipmentRulesModalWrapper)
    }
}
